import { BellRing, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useFlashMessages } from "../api";
import { useAuthStore } from "@/lib/auth/store";

interface Props {
  moduleName: string;
  action?: string;
}

export function FlashBanner({
  moduleName,
  action = "view",
}: Props) {
  const { data, isLoading } = useFlashMessages();
  const name = useAuthStore((s) => s.name ?? s.username ?? '');

  const storageKey = `flash_shown:${moduleName}:${action}`;
  const [show, setShow] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const message = data?.find(
    (m) =>
      m.moduleName === moduleName &&
      m.action === action
  );

  // Show only when: data loaded + name available + not already shown this session
  useEffect(() => {
    if (isLoading || !message || !name) return;
    if (sessionStorage.getItem(storageKey) === '1') return;

    // Mark shown and display
    sessionStorage.setItem(storageKey, '1');
    setShow(true);

    timerRef.current = setTimeout(() => {
      setShow(false);
    }, 5000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isLoading, message, name, storageKey]);

  if (!message || !name) return null;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -25, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="
            fixed top-6 right-6 z-[9999] w-[360px] overflow-hidden
            rounded-2xl border border-white/30 bg-white/75
            backdrop-blur-2xl shadow-[0_15px_45px_rgba(0,0,0,.18)]
          "
        >
          <div className="flex items-start gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10">
              <BellRing className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-[15px] font-semibold text-slate-900">
                Notification
              </h3>
              <p className="mt-1 line-clamp-2 text-[13px] leading-5 text-slate-600">
                Welcome {name}
              </p>
              <span className="mt-2 block text-[11px] text-slate-400">
                Just now
              </span>
            </div>
            <button
              onClick={() => {
                setShow(false);
                if (timerRef.current) clearTimeout(timerRef.current);
              }}
              className="rounded-full p-1 text-slate-400 transition hover:bg-black/5 hover:text-slate-700"
            >
              <X size={15} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}